/**
 * ProviderSidebar — Phase 2 vertical nav for the PooPartner shell.
 *
 * Constant-dark chrome — sidebar bg hardcoded `#374751` (NOT
 * `bg-pp-slate`, which flips to `#E2E6EA` in dark mode and would
 * destroy contrast against the hardcoded `text-white/75` inactive
 * nav items). Both fg AND bg are constant across light/dark themes
 * by design — matches the parallel treatment in AdminShell.jsx.
 *
 * VR-note (2026-02-27): inactive nav uses solid `#D5DEE4` for AAA
 * parity with `AdminShell.jsx` (matching 7.06 : 1 contrast). If the
 * sidebar bg ever needs to follow OS theme, also flip the inactive
 * text + icon color via a token; today both fg AND bg are constant
 * so the contrast invariant holds.
 *
 * Phase 3 §5 — exports `SIDEBAR_ITEMS` and `ProviderSidebarNavBody`
 * so the mobile-drawer Sheet inside ProviderShell can render the
 * same items without duplication. The desktop variant exported as
 * the default is wrapped in `hidden md:flex` so it disappears below
 * the `md:` breakpoint.
 */
import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useConfig } from "@/context/ConfigContext";
import {
  CalendarDays,
  DollarSign,
  MapPin,
  Briefcase,
  Settings,
  Star,
  BarChart3,
} from "lucide-react";
import { cn } from "@/lib/utils";

export const SIDEBAR_ITEMS = [
  { id: "schedule", label: "Schedule", to: "/dashboard/provider/schedule", icon: CalendarDays },
  { id: "earnings", label: "Earnings", to: "/dashboard/provider/earnings", icon: DollarSign },
  // Pluckonomics — partner-side analytics + freemium gating tab.
  // Lives between Earnings and Reviews so the partner sees their
  // payout-framed snapshot next to the lifetime/last-30-days tile.
  { id: "pluckonomics", label: "Pluckonomics", to: "/dashboard/provider/pluckonomics", icon: BarChart3 },
  // Rate My Pluck — between Earnings and Coverage per Step 1 §11 placement spec.
  { id: "reviews",  label: "Reviews",  to: "/dashboard/provider/reviews",  icon: Star },
  { id: "coverage", label: "Coverage", to: "/dashboard/provider/coverage", icon: MapPin },
  { id: "profile",  label: "Profile",  to: "/dashboard/provider/profile",  icon: Briefcase },
  { id: "settings", label: "Settings", to: "/dashboard/provider/settings", icon: Settings },
];

export function ProviderSidebarBrand() {
  const { providerDisplayLabel } = useConfig();
  return (
    <Link
      to="/dashboard/provider"
      className="block px-6 py-6 select-none"
      data-testid="provider-shell-sidebar-brand"
      aria-label="PooPluckers PooPartner home"
    >
      <span
        className="text-[22px] leading-none tracking-[-0.02em]"
        style={{ fontFamily: '"Manrope", system-ui, sans-serif', fontWeight: 800 }}
      >
        <span style={{ color: "var(--pp-orange)" }}>Poo</span>
        <span style={{ color: "white" }}>Pluckers</span>
      </span>
      <span className="mt-1 block text-[11px] font-semibold uppercase tracking-[0.14em] text-white/60">
        {providerDisplayLabel}
      </span>
    </Link>
  );
}

export function ProviderSidebarNavBody({ onItemClick, mobile = false }) {
  const { pathname } = useLocation();
  return (
    <ul className={cn("flex flex-col gap-0.5", mobile && "mt-2")}>
      {SIDEBAR_ITEMS.map((it) => {
        const Icon = it.icon;
        const isActive =
          pathname === it.to || pathname.startsWith(`${it.to}/`);
        return (
          <li key={it.id}>
            <NavLink
              to={it.to}
              onClick={onItemClick}
              className={cn(
                "group flex w-full items-center gap-3 rounded-r-lg border-l-[3px] font-medium transition-colors",
                mobile ? "px-4 py-3 text-[17px]" : "px-4 py-2.5 text-[14px]",
                isActive
                  ? "border-pp-orange bg-white/10 text-white"
                  : "border-transparent text-[#D5DEE4] hover:bg-white/5 hover:text-white"
              )}
              data-testid={`provider-shell-sidebar-${it.id}`}
              data-active={isActive || undefined}
              aria-current={isActive ? "page" : undefined}
            >
              <Icon
                className={cn(
                  "h-4 w-4 shrink-0 transition-colors",
                  isActive ? "text-pp-orange" : "text-[#D5DEE4] group-hover:text-white"
                )}
                aria-hidden="true"
              />
              <span className="truncate">{it.label}</span>
            </NavLink>
          </li>
        );
      })}
    </ul>
  );
}

export default function ProviderSidebar() {
  return (
    <aside
      className="hidden md:flex w-[240px] shrink-0 bg-[#374751] text-white flex-col"
      data-testid="provider-shell-sidebar"
      aria-label="PooPartner navigation"
    >
      <ProviderSidebarBrand />
      <nav className="flex-1 px-3 pb-6" role="navigation">
        <ProviderSidebarNavBody />
      </nav>
    </aside>
  );
}
